import { customComponentStyle, styles } from '../view/styles';
import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { OverlayPositionX, SelectEvents, SelectOption } from '../types';
import '@vaadin/vaadin-context-menu/vaadin-context-menu';
import '@vaadin/vaadin-list-box/vaadin-list-box';
import { ListBoxElement } from '@vaadin/vaadin-list-box';
import { SelectOverlayElement } from './SelectOverlay';
import { GzTooltip } from '../../gzTooltip';

@customElement('gz-select')
export class GzSelectWebComponent extends LitElement {
  @property({ type: Boolean, attribute: true, reflect: true })
  open = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isLoading = false;

  // setting this to TRUE will close the dropdown when the user hovers outside ListBoxElement
  @property({ type: Boolean, attribute: true })
  closeOnMouseLeave = false;

  @property({ type: Array, attribute: true, reflect: true })
  options: Array<SelectOption>;

  @property({ type: String, attribute: true, reflect: true })
  overlayPositionX: OverlayPositionX = OverlayPositionX.RIGHT;

  @query('#overlay')
  overlay!: SelectOverlayElement;

  @query('.trigger')
  positionTarget!: HTMLElement;

  @query('slot')
  private slotElement: HTMLSlotElement;

  private readonly _boundOutsideClickListener: EventListener;
  private readonly _boundSelectedChangedListener: EventListener;
  private readonly _boundScrollEventListener: EventListener;
  private readonly _boundMouseLeaveEventListener: EventListener;
  private readonly _boundResizeListener: EventListener;
  private overlayVerticalSpacer = 4; // the vertical distance between the target and the select overlay
  private overlayMinWidth = 160; // pixels
  static get styles() {
    return styles;
  }

  constructor() {
    super();
    this._boundOutsideClickListener = this.outsideClickListener.bind(this);
    this._boundSelectedChangedListener = this.onListItemSelected.bind(this);
    this._boundScrollEventListener = this.onScrollEventListener.bind(this);
    this._boundMouseLeaveEventListener = this.onMouseLeaveEventListener.bind(this);
    this._boundResizeListener = this.onResizeListener.bind(this);
  }

  protected updated(properties: PropertyValues): void {
    if (properties.has('open') && !this.open) {
      this.dispatchEvent(new CustomEvent(SelectEvents.OVERLAY_CLOSED));
    }

    if (properties.has('isLoading')) {
      const overlay = window.document.body.querySelector('select-overlay#overlay') as SelectOverlayElement;
      if (overlay) {
        this.renderOverlayContent(overlay);
      }
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.open = false;
  }

  render(): TemplateResult {
    return html`
      ${customComponentStyle}
      <span class="trigger" @click="${this.createOverlayContent}">
        <slot></slot>
      </span>
      <select-overlay
        id="overlay"
        .opened="${this.open}"
        @vaadin-overlay-open="${this.overlayOpenListener}"
        @opened-changed="${this.onOpenedChanged}"
      ></select-overlay>
    `;
  }

  private renderLoadingTemplate = (overlay: SelectOverlayElement): void => {
    overlay.renderer = function (root) {
      root.innerHTML = `<div class="loading-wrapper">
        <gz-progress-spinner spinnerwidth="small" class="spinner"></gz-progress-spinner>
      </div>`;
    };
  };

  private createListItem(option: SelectOption): HTMLElement {
    const item = window.document.createElement('vaadin-item');
    item.textContent = option.title;

    // Apply optional properties if they exist
    if (option.customClass) {
      item.classList.add(option.customClass);
    }
    if (option.id) {
      item.id = option.id;
    }
    if (option.disabled) {
      item.setAttribute('disabled', JSON.stringify(option.disabled));
    }

    item.dataset.value = option.value.toString();

    // Setup tooltip events if tooltip text is provided
    if (option.tooltipText) {
      item.addEventListener('mousemove', e => GzTooltip.show(<string>option.tooltipText, e));
      item.addEventListener('mouseleave', GzTooltip.hide);
    }

    return item;
  }

  private createListItems = (overlay: SelectOverlayElement, options: SelectOption[]): void => {
    overlay.renderer = root => {
      let listBox = root.firstElementChild as ListBoxElement;

      // Check if there is a list-box generated with the previous renderer call to update its content instead of recreation
      if (listBox) {
        listBox.innerHTML = '';
      } else {
        listBox = window.document.createElement('vaadin-list-box') as ListBoxElement;

        options.forEach(option => listBox.appendChild(this.createListItem(option)));
        root.appendChild(listBox);
      }
    };
  };

  private createOverlayContent(): void {
    if (this.open) {
      this.open = false;
      return;
    }

    this.renderOverlayContent(this.overlay);
    this.open = !this.open;
    this.setOverlayStyles();

    // this.setOverlayPosition(this.overlay);

    window.addEventListener('mousedown', this._boundOutsideClickListener, true);
    window.addEventListener('wheel', this._boundScrollEventListener, true);
    window.addEventListener('scroll', this._boundScrollEventListener, true);
    window.addEventListener('resize', this._boundResizeListener, true);
    this.overlay.addEventListener('selected-changed', this._boundSelectedChangedListener, true);
    this.setActiveState();
  }

  private setActiveState(): void {
    this.slotElement.assignedElements()[0].classList.add('active');
  }

  private removeActiveState(): void {
    this.slotElement.assignedElements()[0].classList.remove('active');
  }

  private onListItemSelected(e: CustomEvent): void {
    const selectedOption = this.options[e.detail.value];
    const event = new CustomEvent(SelectEvents.SELECTION_CHANGED, {
      detail: {
        selected: selectedOption.value,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(event);
    this.open = false;
    this.removeActiveState();
  }

  private alignOverlayToTarget(overlay: SelectOverlayElement): void {
    let translateX = 0;
    let translateY = 0;

    const targetRect = this.positionTarget.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    const shouldAlignAbove = this.shouldAlignAbove(targetRect);
    const devicePixelRatio = window.devicePixelRatio || 1;

    if (this.overlayPositionX === OverlayPositionX.LEFT) {
      translateX = targetRect.left;
    } else {
      translateX = targetRect.left - (overlayRect.width - targetRect.width) + (translateX || 0);
    }

    translateX = Math.round(translateX * devicePixelRatio) / devicePixelRatio;
    translateY = targetRect.top + (translateY || 0) + targetRect.height;
    translateY = Math.round(translateY * devicePixelRatio) / devicePixelRatio;

    if (shouldAlignAbove) {
      const verticalOffset = overlayRect.height + targetRect.height + this.overlayVerticalSpacer;
      overlay.style.top = `-${verticalOffset}px`;
    } else {
      overlay.style.top = `${this.overlayVerticalSpacer}px`;
    }

    overlay.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
  }

  private outsideClickListener(event): void {
    const eventPath = event.composedPath();
    if (
      eventPath.indexOf(this.positionTarget) > 0 ||
      eventPath.indexOf(this.overlay) > 0 ||
      eventPath.indexOf(window.document.querySelector('vaadin-list-box')) > 0
    ) {
      return;
    }

    this.open = false;
    this.removeActiveState();
  }

  private onScrollEventListener(): void {
    this.open = false;
    this.removeActiveState();
  }

  private overlayOpenListener(): void {
    if (document.body.style.pointerEvents === 'none') {
      document.body.style.pointerEvents = 'auto';
    }

    if (!this.closeOnMouseLeave) {
      return;
    }
    const listBox = document.querySelector('vaadin-list-box');
    if (listBox) {
      listBox.addEventListener('mouseleave', this._boundMouseLeaveEventListener, false);
    }
  }

  private onMouseLeaveEventListener(): void {
    if (this.open) {
      this.open = false;
      this.positionTarget.blur();
    }
  }

  private onResizeListener(): void {
    this.open = false;
    this.removeActiveState();
  }

  private shouldAlignAbove(targetClientRect: DOMRect): boolean {
    const spaceBelow =
      (window.innerHeight - targetClientRect.bottom - Math.min(document.body.scrollTop, 0)) / window.innerHeight;
    return spaceBelow < 0.3;
  }

  private onOpenedChanged(): void {
    if (!this.open) {
      return;
    }

    const overlay = window.document.body.querySelector('select-overlay') as SelectOverlayElement;
    if (!overlay) {
      return;
    }

    // the overlay element height and width are available only after the overlay is added to the window element, so we
    // need to perform the vertical and horizontal alignments after the overlay is inserted in the window
    this.updateOverlayVisibilityAndPosition(overlay);
  }

  private updateOverlayVisibilityAndPosition(overlay: SelectOverlayElement): void {
    this.alignOverlayToTarget(overlay);
    overlay.style.opacity = '1';
  }

  private renderOverlayContent(overlay: SelectOverlayElement): void {
    if (this.isLoading) {
      this.renderLoadingTemplate(overlay);
    } else if (!this.options) {
      overlay.renderer = function (root) {
        root.innerHTML = `<div style="min-height: 40px"></div>`;
      };
    } else {
      this.createListItems(overlay, this.options);
    }
  }

  private setOverlayStyles() {
    const triggerButtonWidth = this.positionTarget.getBoundingClientRect().width;
    this.overlay.style.minWidth = Math.max(triggerButtonWidth, this.overlayMinWidth) + 'px';
    this.overlay.style.position = 'fixed';
  }
}
